/**
 * useFlag — runtime consumer of the FLAG_REGISTRY SSOT.
 *
 * The registry (`registry.js`) is the lint-time + ownership SSOT;
 * this hook is the runtime SSOT — given a registered key it answers
 * "is this flag on right now?" so consumers don't grow ad-hoc
 * `window.location.search.includes('flag=...')` snippets.
 *
 * Resolution order (highest to lowest precedence):
 *   1. `?ff_<key>=1` / `?ff_<key>=0` — URL query toggle. Lets QA flip
 *      a flag on a single device without touching localStorage or
 *      shipping a deploy. `1` / `true` / `on` enable; `0` / `false` /
 *      `off` disable.
 *   2. `localStorage.getItem('ff.<key>')` — persistent override per
 *      browser. Same enable/disable vocab as the query string.
 *   3. Registry `ramp` annotation — when the entry's `ramp` contains
 *      `100%` (full prod ramp shorthand), treat as ON. Lower ramps
 *      (e.g. `0% prod`, `10% prod`) stay OFF at runtime; the per-
 *      device override path is what flips them per-tester. This keeps
 *      "registered but not yet ramped" the default and prevents a
 *      casual ramp annotation from silently enabling a flag in prod.
 *   4. Deprecated / retire- entries → always OFF. The ESLint rule
 *      flags the call site separately; the runtime stays safe.
 *   5. Default OFF. Unregistered keys also resolve OFF (defensive —
 *      a typo never silently turns a feature on).
 *
 * The hook is dependency-light by design: no React context, no
 * eventual-consistency listener. Toggling a flag from the URL or
 * localStorage requires a page reload (the typical QA workflow);
 * keeping reads synchronous means consumer renders stay
 * deterministic without an extra subscription tier.
 */

import { useMemo } from 'react';
import { FLAG_REGISTRY } from './registry.js';
import { captureError } from '../errors/captureError';

const TRUTHY = new Set(['1', 'true', 'on', 'yes']);
const FALSY = new Set(['0', 'false', 'off', 'no']);

function parseOverride(raw: string | null | undefined): boolean | null {
    if (raw == null) return null;
    const lowered = raw.toLowerCase();
    if (TRUTHY.has(lowered)) return true;
    if (FALSY.has(lowered)) return false;
    return null;
}

function readQueryOverride(key: string): boolean | null {
    if (typeof window === 'undefined') return null;
    try {
        const params = new URLSearchParams(window.location.search);
        return parseOverride(params.get(`ff_${key}`));
    } catch (err) {
        captureError(err, { scope: 'useFlag', op: `query:${key}` });
        return null;
    }
}

function readStorageOverride(key: string): boolean | null {
    if (typeof window === 'undefined') return null;
    try {
        return parseOverride(window.localStorage.getItem(`ff.${key}`));
    } catch (err) {
        captureError(err, { scope: 'useFlag', op: `localStorage:${key}` });
        return null;
    }
}

/** Pure resolver — exported for tests. */
export function resolveFlag(key: string): boolean {
    const queryOverride = readQueryOverride(key);
    if (queryOverride !== null) return queryOverride;

    const storageOverride = readStorageOverride(key);
    if (storageOverride !== null) return storageOverride;

    const entry = (FLAG_REGISTRY as Record<string, { ramp?: string; deprecated?: boolean } | undefined>)[key];
    if (!entry) return false;
    if (entry.deprecated) return false;
    if (key.startsWith('retire-')) return false;

    // Ramp annotation is the registry-level on-switch only when it
    // explicitly declares full graduation (`100%`). Partial ramps
    // (0%, 10%, internal-only) document the rollout intent but stay
    // OFF at runtime — per-device flips are how testers and QA opt
    // in until the ramp fully graduates.
    return typeof entry.ramp === 'string' && /\b100\s*%/.test(entry.ramp);
}

/** Runtime feature-flag check. See module header for resolution order. */
export function useFlag(key: string): boolean {
    return useMemo(() => resolveFlag(key), [key]);
}

/** Non-hook variant for service / event-handler call sites. Same
 *  resolver as `useFlag`. */
export function isFlagEnabled(key: string): boolean {
    return resolveFlag(key);
}
